import { Card, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { TrendingUp } from 'lucide-react';
import { displayDate } from '../../lib/dates';

const pill = (value, classes) => (
  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-medium ${classes}`}>
    {value}
  </span>
);

export const WageReportTable = ({ wageReport, totals, fmt, reportLoading, dateRange }) => (
  <Card className="bg-white border border-[#D1D1CD] rounded-lg shadow-sm overflow-hidden">
    <CardHeader>
      <CardTitle className="text-lg font-semibold text-[#1C1C1E] flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-[#2E4632]" />
        Wage Report
        <span className="text-sm font-normal text-[#5C5C5C] ml-2">
          ({dateRange.start_date} to {dateRange.end_date})
        </span>
      </CardTitle>
    </CardHeader>
    <div className="overflow-x-auto">
      <p className="md:hidden px-4 py-2 text-xs text-[#5C5C5C] bg-[#F4F4F0] border-t border-[#D1D1CD]">
        Swipe horizontally to see all columns →
      </p>
      {reportLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E4632]"></div>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F4F0]">
              <TableHead className="font-semibold text-[#1C1C1E]">Worker</TableHead>
              <TableHead className="font-semibold text-[#1C1C1E] text-center">Full Days</TableHead>
              <TableHead className="font-semibold text-[#1C1C1E] text-center">Half Days</TableHead>
              <TableHead className="font-semibold text-[#1C1C1E] text-center">Absent</TableHead>
              <TableHead className="font-semibold text-[#1C1C1E] text-center">OT (hrs)</TableHead>
              <TableHead className="font-semibold text-[#1C1C1E] text-right">Gross</TableHead>
              <TableHead className="font-semibold text-[#1C1C1E] text-right">Advance</TableHead>
              <TableHead className="font-semibold text-[#1C1C1E] text-right">Payout</TableHead>
              <TableHead className="font-semibold text-[#1C1C1E]">Payment Dates</TableHead>
              <TableHead className="font-semibold text-[#1C1C1E] text-right">Net (Period)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {wageReport.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-[#5C5C5C]">
                  No data for selected period
                </TableCell>
              </TableRow>
            ) : (
              <>
                {wageReport.map((report) => (
                  <TableRow key={report.worker_id} data-testid={`report-row-${report.worker_id}`}>
                    <TableCell className="font-medium text-[#1C1C1E]">
                      {report.worker_name}
                    </TableCell>
                    <TableCell className="text-center">
                      {pill(report.full_days, 'bg-[#2E4632]/10 text-[#2E4632]')}
                    </TableCell>
                    <TableCell className="text-center">
                      {pill(report.half_days, 'bg-[#D97706]/10 text-[#D97706]')}
                    </TableCell>
                    <TableCell className="text-center">
                      {pill(report.absent_days, 'bg-[#BC4749]/10 text-[#BC4749]')}
                    </TableCell>
                    <TableCell className="text-center text-[#5C5C5C]">
                      {report.overtime_hours}
                    </TableCell>
                    <TableCell className="text-right font-medium text-[#1C1C1E]">
                      {fmt(report.gross_wage)}
                    </TableCell>
                    <TableCell className="text-right text-[#BC4749]">
                      {report.advance_deduction > 0 ? `-${fmt(report.advance_deduction)}` : fmt(0)}
                    </TableCell>
                    <TableCell className="text-right text-[#2E4632]">
                      {report.payout_total > 0 ? `-${fmt(report.payout_total)}` : fmt(0)}
                    </TableCell>
                    <TableCell className="text-[#5C5C5C] text-xs max-w-[160px]" data-testid={`payment-dates-${report.worker_id}`}>
                      {report.payout_dates && report.payout_dates.length > 0
                        ? report.payout_dates.map(d => displayDate(d)).join(', ')
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right font-bold text-[#2E4632]">
                      {fmt(report.net_wage)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-[#F4F4F0] font-semibold">
                  <TableCell className="text-[#1C1C1E]">TOTAL</TableCell>
                  <TableCell className="text-center text-[#2E4632]">{totals.full_days}</TableCell>
                  <TableCell className="text-center text-[#D97706]">{totals.half_days}</TableCell>
                  <TableCell className="text-center text-[#BC4749]">{totals.absent_days}</TableCell>
                  <TableCell className="text-center text-[#5C5C5C]">{totals.overtime_hours}</TableCell>
                  <TableCell className="text-right text-[#1C1C1E]">{fmt(totals.gross_wage)}</TableCell>
                  <TableCell className="text-right text-[#BC4749]">{totals.advance_deduction > 0 ? `-${fmt(totals.advance_deduction)}` : fmt(0)}</TableCell>
                  <TableCell className="text-right text-[#2E4632]">{totals.payout_total > 0 ? `-${fmt(totals.payout_total)}` : fmt(0)}</TableCell>
                  <TableCell className="text-[#5C5C5C]">-</TableCell>
                  <TableCell className="text-right text-[#2E4632]">{fmt(totals.net_wage)}</TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  </Card>
);
